import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import LogService from '../services/LogService';
import { LogEntry, LogQueryRequest, LogQueryResponse } from '../types/LogTypes';
import './LogViewer.css';

type SortField = 'logTimestamp' | 'logType' | 'customer' | 'service' | 'environment' | 'instance' | 'message';
type SortDirection = 'asc' | 'desc';

const LogViewer: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [customers, setCustomers] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [environments, setEnvironments] = useState<string[]>([]);
  const [logTypes, setLogTypes] = useState<string[]>([]);
  
  const [filters, setFilters] = useState<LogQueryRequest>({
    page: 1,
    pageSize: 50,
  });
  
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [sortField, setSortField] = useState<SortField>('logTimestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [initialLoad, setInitialLoad] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<LogEntry | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEntry(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadFilterOptions = async () => {
    try {
      const [customersData, servicesData, environmentsData, logTypesData] = await Promise.all([
        LogService.getCustomers(),
        LogService.getServices(),
        LogService.getEnvironments(),
        LogService.getLogTypes(),
      ]);
      setCustomers(customersData || []);
      setServices(servicesData || []);
      setEnvironments(environmentsData || []);
      setLogTypes(logTypesData || []);
    } catch (err) {
      console.error('Failed to load filter options:', err);
      // Set empty arrays on error to prevent undefined
      setCustomers([]);
      setServices([]);
      setEnvironments([]);
      setLogTypes([]);
    }
  };

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const queryRequest = {
        ...filters,
        sortField,
        sortDirection
      };
      const response: LogQueryResponse = await LogService.queryLogs(queryRequest);
      setLogs(response.logs || []);
      setTotalCount(response.totalCount || 0);
      setTotalPages(response.totalPages || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs');
      setLogs([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [filters, sortField, sortDirection]);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Apply filters from URL query parameters on initial load
  useEffect(() => {
    const customer = searchParams.get('customer');
    const service = searchParams.get('service');
    const environment = searchParams.get('environment');
    const logType = searchParams.get('logType');
    const searchText = searchParams.get('searchText');

    if (customer || service || environment || logType || searchText) {
      setFilters(prev => ({
        ...prev,
        customer: customer || undefined,
        service: service || undefined,
        environment: environment || undefined,
        logType: logType || undefined,
        searchText: searchText || undefined,
        page: 1,
      }));
      setInitialLoad(false);
    } else if (initialLoad) {
      // Only load on initial mount if no URL parameters
      setInitialLoad(false);
    }
  }, [searchParams, initialLoad]);

  useEffect(() => {
    if (!initialLoad) {
      loadLogs();
    }
  }, [filters.page, filters.customer, filters.service, filters.environment, filters.logType, filters.startDate, filters.endDate, filters.searchText, filters.pageSize, sortField, sortDirection, initialLoad, loadLogs]);

  const handleSort = (field: SortField) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <span className="sort-icon sort-neutral"></span>;
    }
    return sortDirection === 'asc' 
      ? <span className="sort-icon sort-asc"></span>
      : <span className="sort-icon sort-desc"></span>;
  };

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleQuickDateRange = (range: 'lastHour' | 'last4Hours' | 'today' | 'yesterday' | 'thisWeek' | 'last7Days') => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    switch (range) {
      case 'lastHour':
        startDate = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case 'last4Hours':
        startDate = new Date(now.getTime() - 4 * 60 * 60 * 1000);
        break;
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        break;
      case 'thisWeek':
        const dayOfWeek = now.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday as start of week
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff, 0, 0, 0);
        break;
      case 'last7Days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      default:
        return;
    }

    setFilters(prev => ({
      ...prev,
      startDate: formatDateTimeLocal(startDate),
      endDate: formatDateTimeLocal(endDate),
      page: 1,
    }));
  };

  const handleFilterChange = (field: keyof LogQueryRequest, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filters change
    }));
    
    // Clear URL parameters when user manually changes filters
    setSearchParams({});
  };

  const handleSearch = () => {
    loadLogs();
  };

  const handleReset = () => {
    setFilters({
      page: 1,
      pageSize: 50,
    });
    setSearchParams({});
    setTimeout(() => loadLogs(), 0);
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    } catch {
      return 'Invalid Date';
    }
  };

  const getLogTypeClass = (logType: string) => {
    if (!logType) return 'log-type-default';
    const type = logType.toLowerCase();
    if (type.includes('error')) return 'log-type-error';
    if (type.includes('warning')) return 'log-type-warning';
    if (type.includes('info')) return 'log-type-info';
    return 'log-type-default';
  };

  return (
    <div className="log-viewer">
      <h1>Log Viewer</h1>

      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label>Customer</label>
            <select
              value={filters.customer || ''}
              onChange={(e) => handleFilterChange('customer', e.target.value || undefined)}
            >
              <option value="">All Customers</option>
              {(customers || []).map(customer => (
                <option key={customer} value={customer}>{customer}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Service</label>
            <select
              value={filters.service || ''}
              onChange={(e) => handleFilterChange('service', e.target.value || undefined)}
            >
              <option value="">All Services</option>
              {(services || []).map(service => (
                <option key={service} value={service}>{service}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Environment</label>
            <select
              value={filters.environment || ''}
              onChange={(e) => handleFilterChange('environment', e.target.value || undefined)}
            >
              <option value="">All Environments</option>
              {(environments || []).map(env => (
                <option key={env} value={env}>{env}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Log Type</label>
            <select
              value={filters.logType || ''}
              onChange={(e) => handleFilterChange('logType', e.target.value || undefined)}
            >
              <option value="">All Log Types</option>
              {(logTypes || []).map(logType => (
                <option key={logType} value={logType}>{logType}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              value={filters.startDate || ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value || undefined)}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={filters.endDate || ''}
              onChange={(e) => handleFilterChange('endDate', e.target.value || undefined)}
            />
          </div>

          <div className="filter-group">
            <label>Search Text</label>
            <input
              type="text"
              placeholder="Search in messages..."
              value={filters.searchText || ''}
              onChange={(e) => handleFilterChange('searchText', e.target.value || undefined)}
            />
          </div>

          <div className="filter-group">
            <label>Page Size</label>
            <select
              value={filters.pageSize}
              onChange={(e) => handleFilterChange('pageSize', parseInt(e.target.value))}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>
        </div>

        <div className="date-range-buttons">
          <label>Quick Date Ranges:</label>
          <div className="button-group">
            <button onClick={() => handleQuickDateRange('lastHour')} className="btn-date-range">Last Hour</button>
            <button onClick={() => handleQuickDateRange('last4Hours')} className="btn-date-range">Last 4 Hours</button>
            <button onClick={() => handleQuickDateRange('today')} className="btn-date-range">Today</button>
            <button onClick={() => handleQuickDateRange('yesterday')} className="btn-date-range">Yesterday</button>
            <button onClick={() => handleQuickDateRange('thisWeek')} className="btn-date-range">This Week</button>
            <button onClick={() => handleQuickDateRange('last7Days')} className="btn-date-range">Last 7 Days</button>
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={handleSearch} className="btn-search">Search</button>
          <button onClick={handleReset} className="btn-reset">Reset</button>
        </div>
      </div>

      {loading && <div className="loading">Loading logs...</div>}
      
      {error && (
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={loadLogs}>Retry</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="results-info">
            <p>
              Showing {(logs || []).length} of {(totalCount || 0).toLocaleString()} logs
              {filters.page && totalPages > 0 && ` (Page ${filters.page} of ${totalPages})`}
            </p>
          </div>

          <div className="log-table-container">
            <table className="log-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('logTimestamp')} className="sortable">
                    Timestamp {getSortIcon('logTimestamp')}
                  </th>
                  <th onClick={() => handleSort('logType')} className="sortable">
                    Type {getSortIcon('logType')}
                  </th>
                  <th onClick={() => handleSort('customer')} className="sortable">
                    Customer {getSortIcon('customer')}
                  </th>
                  <th onClick={() => handleSort('service')} className="sortable">
                    Service {getSortIcon('service')}
                  </th>
                  <th onClick={() => handleSort('environment')} className="sortable">
                    Environment {getSortIcon('environment')}
                  </th>
                  <th onClick={() => handleSort('instance')} className="sortable">
                    Instance {getSortIcon('instance')}
                  </th>
                  <th onClick={() => handleSort('message')} className="sortable">
                    Message {getSortIcon('message')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(!logs || logs.length === 0) ? (
                  <tr>
                    <td colSpan={7} className="no-data">No logs found</td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={`${log.partitionKey}-${log.rowKey}`} onClick={() => setSelectedEntry(log)}>
                      <td className="log-timestamp">{formatDate(log.logTimestamp)}</td>
                      <td>
                        <span className={`log-type ${getLogTypeClass(log.logType)}`}>
                          {log.logType || 'N/A'}
                        </span>
                      </td>
                      <td>{log.customer || 'N/A'}</td>
                      <td>{log.service || 'N/A'}</td>
                      <td>{log.environment || 'N/A'}</td>
                      <td className="log-instance">{log.instance}</td>
                      <td className="log-message">{log.message || 'N/A'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange((filters.page || 1) - 1)}
                disabled={filters.page === 1}
                className="btn-page"
              >
                Previous
              </button>
              
              <span className="page-info">
                Page {filters.page || 1} of {totalPages}
              </span>
              
              <button
                onClick={() => handlePageChange((filters.page || 1) + 1)}
                disabled={filters.page === totalPages}
                className="btn-page"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      {selectedEntry && (
        <div className="modal-overlay" onClick={() => setSelectedEntry(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Log Entry Detail</h2>
              <button className="modal-close" onClick={() => setSelectedEntry(null)}>&times;</button>
            </div>
            <div className="modal-body">

              <div className="modal-section">
                <div className="modal-section-title">Summary</div>
                <div className="modal-fields">
                  <span className="modal-field-label">Timestamp</span>
                  <span className="modal-field-value">{formatDate(selectedEntry.logTimestamp)}</span>
                  <span className="modal-field-label">Type</span>
                  <span className="modal-field-value">
                    <span className={`log-type ${getLogTypeClass(selectedEntry.logType)}`}>{selectedEntry.logType || 'N/A'}</span>
                  </span>
                  <span className="modal-field-label">Customer</span>
                  <span className="modal-field-value">{selectedEntry.customer || 'N/A'}</span>
                  <span className="modal-field-label">Service</span>
                  <span className="modal-field-value">{selectedEntry.service || 'N/A'}</span>
                  <span className="modal-field-label">Environment</span>
                  <span className="modal-field-value">{selectedEntry.environment || 'N/A'}</span>
                  <span className="modal-field-label">Instance</span>
                  <span className="modal-field-value">{selectedEntry.instance}</span>
                </div>
              </div>

              <div className="modal-section">
                <div className="modal-section-title">Message</div>
                <div className="modal-fields">
                  <span className="modal-field-label">Message</span>
                  <span className="modal-field-value wrap">{selectedEntry.message || 'N/A'}</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogViewer;
